import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { FileText, Users, ShoppingCart, Megaphone } from "lucide-react";
import { InviteColleagueDialog } from "@/components/InviteColleagueDialog";
import { GibletChatDialogEnhanced } from "@/components/chat/GibletChatDialogEnhanced";
import sprintButton from "@/assets/icons/sprint-hands.png";

export const NavbarActionButtons = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [gibletChatOpen, setGibletChatOpen] = useState(false);
  const [selectedCallType, setSelectedCallType] = useState<'solution' | 'knowledge' | 'destruction' | 'start-call-flow' | 'create-kb' | 'team-sprint' | null>(null);

  // Determine channel context from current route
  const getChannelContext = () => {
    if (location.pathname.includes('curt')) {
      return { channelId: 'ec9b3b57-263a-4713-b3ed-5fe67808486e', channelName: 'CURT' };
    } else if (location.pathname.includes('usa')) {
      return { channelId: '536ba1bb-4a0a-4024-a19c-f77b566bd860', channelName: 'US Federal Government' };
    } else if (location.pathname.includes('oil-gas')) {
      return { channelId: '69d024ae-f179-40d9-9ac5-88aaf46ce866', channelName: 'Oil & Gas' };
    }
    return { channelId: '28570003-dd67-4842-bc21-784df8caaf94', channelName: 'iSPAI' };
  };

  const { channelId, channelName } = getChannelContext();

  const handleCallTypeSelect = (type: 'solution' | 'knowledge' | 'destruction') => {
    setSelectedCallType(type);
    setGibletChatOpen(true);
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={() => {
                setSelectedCallType('create-kb');
                setGibletChatOpen(true);
              }}
              size="icon"
              className="h-10 w-10 bg-green-700 hover:bg-green-700 text-white"
              aria-label="Create a new knowledge base"
            >
              <FileText className="h-6 w-6" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Create a new knowledge base</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={() => {
                setSelectedCallType('team-sprint');
                setGibletChatOpen(true);
              }}
              size="icon"
              className="h-10 w-10 bg-green-700 hover:bg-green-700 text-white"
              aria-label="Launch a Sprint"
            >
              <img src={sprintButton} alt="Launch a Sprint" className="h-7 w-7 object-contain" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Collaborate with your network to advance prompts that build better knowledge bases for AI</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={() => {
                setSelectedCallType('start-call-flow');
                setGibletChatOpen(true);
              }}
              size="icon"
              className="h-10 w-10 bg-green-700 hover:bg-green-700 text-white"
              aria-label="Initiate a Call"
            >
              <Megaphone className="h-6 w-6" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Create a Call for Solutions, Knowledge, or Destruction</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={() => setInviteDialogOpen(true)}
              size="icon"
              className="h-10 w-10 bg-green-700 hover:bg-green-700 text-white"
              aria-label="Invite a colleague"
            >
              <Users className="h-6 w-6" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Invite a colleague to your knowledge network or channel</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={() => navigate("/ispai-solutions")}
              size="icon"
              className="h-10 w-10 bg-green-700 hover:bg-green-700 text-white"
              aria-label="Go to Marketplace"
            >
              <ShoppingCart className="h-6 w-6" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Go to the Marketplace to consume KBs or Solutions that have been minted</p>
          </TooltipContent>
        </Tooltip>
      </div>

      <InviteColleagueDialog
        open={inviteDialogOpen}
        onOpenChange={setInviteDialogOpen}
        defaultChannelId={channelId}
        defaultChannelName={channelName}
      />

      <GibletChatDialogEnhanced
        open={gibletChatOpen}
        onOpenChange={setGibletChatOpen}
        initialCallType={selectedCallType}
      />
    </>
  );
};
